import { useState, useEffect } from "react";
import { DateTime } from "luxon";
import {
  SidePanel,
  SidePanelContent,
  SidePanelHeader,
  SidePanelBody,
  SidePanelFooter,
} from "@/components/ui/SidePanel";
import { Button } from "@/components/ui/Button";
import { useUpdateUserProfileMutation } from "@/api/clubsApi";
import type { UserProfile } from "@/api/clubsApi";
import { cn } from "@/components/ui/cn";

interface EditContactModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  profile: UserProfile;
}

const inputClassName = cn(
  "w-full rounded-2xl border border-border-tertiary p-3",
  "text-base text-text-primary bg-input-background",
  "placeholder:text-text-tertiary",
  "focus:outline-none focus:ring-2 focus:ring-text-primary focus:ring-offset-2 focus:ring-offset-background",
  "focus:border-text-primary"
);

export function EditContactModal({
  open,
  onOpenChange,
  profile,
}: EditContactModalProps) {
  const [updateProfile, { isLoading: isSaving }] = useUpdateUserProfileMutation();

  // Form state
  const [phoneNumber, setPhoneNumber] = useState("");
  const [email, setEmail] = useState("");

  // Initialize form state when modal opens
  useEffect(() => {
    if (open && profile) {
      setPhoneNumber(profile.phone_number || "");
      setEmail(profile.email || "");
    }
  }, [open, profile]);

  const handleSave = async () => {
    try {
      await updateProfile({
        id: profile.id,
        phone_number: phoneNumber.trim() || null,
        email: email.trim() || null,
        updated_at: DateTime.now().toISO(),
      }).unwrap();
      onOpenChange(false);
    } catch (error) {
      console.error("Failed to update profile:", error);
    }
  };

  return (
    <SidePanel open={open} onOpenChange={onOpenChange}>
      <SidePanelContent>
        <SidePanelHeader title="Edit contact" onClose={() => onOpenChange(false)} />

        <SidePanelBody className="space-y-4">
          <div className="space-y-1.5">
            <label className="text-body-3 text-text-secondary">Phone number</label>
            <input
              type="tel"
              value={phoneNumber}
              onChange={(e) => setPhoneNumber(e.target.value)}
              placeholder="(555) 555-5555"
              className={inputClassName}
            />
          </div>

          <div className="space-y-1.5">
            <label className="text-body-3 text-text-secondary">Email</label>
            <input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="you@example.com"
              className={inputClassName}
            />
          </div>
        </SidePanelBody>

        <SidePanelFooter className="flex justify-end gap-3">
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button
            onClick={handleSave}
            disabled={isSaving}
            className="bg-text-primary text-text-reversed hover:bg-text-primary/90"
          >
            {isSaving ? "Saving..." : "Save"}
          </Button>
        </SidePanelFooter>
      </SidePanelContent>
    </SidePanel>
  );
}
